/**
 * Patient appointments: upcoming, pending and history.
 *
 * Replaces `renderPatientAppointments`, `switchPatientAppointmentsTab`,
 * `cancelPatientBooking`, `requestRefundAndCancel`, `viewPatientDepositReceipt`,
 * `updatePatientDepositReceipt` and `uploadPatientDepositReceipt`.
 *
 * المرحلة 1.3 (F05, P20): «تغيير الميعاد» beside «إلغاء» where the booking's
 * institution lets a patient move their own visit (patientReschedule; closed
 * by default, the clinic moves it as today), the channel of a remote visit,
 * the reason a doctor gave for declining, and «قيّم الزيارة» on a finished
 * visit not yet reviewed.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Modal,
  Panel,
  PageHeader,
  Spinner,
  Tabs,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { usePatientBookings, useBookingActions } from '../../hooks/useBookings';
import {
  DEPOSIT_STATE_LABELS,
  REFUND_STATE_LABELS,
  refundState,
  depositState,
  statusLabel,
  statusStyle,
  typeLabel,
} from '../../domain/bookings';
import { formatCurrency } from '../../domain/pricing';
import { arabicDayOf, formatTime12, isoToDisplayDate } from '../../domain/dates';
import { compressImage } from '../../domain/files';
import { rescheduleState } from '../../domain/discovery';
import { useI18n } from '../../i18n/I18nProvider';
import { ModeBadge } from '../discovery/parts';
import { RescheduleDialog } from './RescheduleDialog';
import { ReviewDialog } from './ReviewDialog';

const TABS = [
  { id: 'upcoming', label: 'القادمة', icon: 'calendar-check' },
  { id: 'pending', label: 'بانتظار التأكيد', icon: 'clock' },
  { id: 'past', label: 'السجل', icon: 'history' },
];

export function PatientAppointments() {
  const { patientId } = useAuth();
  const { success, error: toastError } = useNotifications();

  const { buckets, bookings, loading, error, reload, setData } = usePatientBookings(patientId);
  const actions = useBookingActions({ bookings, setBookings: setData });

  const [tab, setTab] = useState('upcoming');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [receiptTarget, setReceiptTarget] = useState(null);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);

  const tabsWithCounts = useMemo(
    () => TABS.map((t) => ({ ...t, count: buckets[t.id]?.length ?? 0 })),
    [buckets],
  );

  const list = buckets[tab] || [];

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await actions.cancel(cancelTarget.id);
      success(cancelTarget.depositPaid ? 'تم إلغاء الحجز — والعربون في انتظار الرد من العيادة' : 'تم إلغاء الحجز');
      // The server records the deposit owed back; read it so the card says so.
      reload();
    } catch (err) {
      toastError(err.message || 'تعذر إلغاء الحجز');
    } finally {
      setCancelTarget(null);
    }
  };

  const handleReceiptUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !receiptTarget) return;
    try {
      const dataUri = await compressImage(file, { maxWidth: 1400, quality: 0.72 });
      await actions.uploadReceipt(receiptTarget.id, dataUri);
      success('تم رفع الإيصال — بانتظار مراجعة العيادة');
      setReceiptTarget(null);
      reload();
    } catch (err) {
      toastError(err.message || 'تعذر رفع الإيصال');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="حجوزاتي"
        subtitle={`${bookings.length} حجز`}
        icon="calendar-check"
        actions={
          <Button variant="ghost" icon="refresh-cw" onClick={reload} loading={loading}>
            تحديث
          </Button>
        }
      />

      <Tabs tabs={tabsWithCounts} active={tab} onChange={setTab} />

      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : loading && bookings.length === 0 ? (
        <Spinner label="جاري تحميل الحجوزات..." />
      ) : list.length === 0 ? (
        <Panel>
          <EmptyState
            icon="calendar-x"
            title="لا توجد حجوزات هنا"
            message={
              tab === 'past'
                ? 'ستظهر هنا زياراتك المكتملة والملغاة.'
                : 'احجز موعداً جديداً من صفحة الأطباء.'
            }
          />
        </Panel>
      ) : (
        <div className="space-y-3">
          {list.map((booking) => (
            <AppointmentCard
              key={booking.id}
              booking={booking}
              busy={String(actions.pendingId) === String(booking.id)}
              onCancel={() => setCancelTarget(booking)}
              onUploadReceipt={() => setReceiptTarget(booking)}
              onViewReceipt={() => setViewingReceipt(booking)}
              onReschedule={() => setMoveTarget(booking)}
              onReview={() => setReviewTarget(booking)}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        destructive
        title="إلغاء الحجز"
        confirmLabel="نعم، ألغِ الحجز"
        message={
          cancelTarget
            ? `هل تريد إلغاء ${typeLabel(cancelTarget.type)} يوم ${isoToDisplayDate(cancelTarget.date)} الساعة ${formatTime12(cancelTarget.time)}؟${
                cancelTarget.depositPaid
                  ? '\n\nتم دفع عربون لهذا الحجز — سيتم مراجعة طلب الاسترداد من العيادة.'
                  : ''
              }`
            : ''
        }
      />

      {/* Upload / replace a deposit receipt */}
      <Modal
        open={Boolean(receiptTarget)}
        onClose={() => setReceiptTarget(null)}
        title="رفع إيصال العربون"
        icon="upload-cloud"
        size="sm"
      >
        {receiptTarget && (
          <div className="space-y-4">
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-400">قيمة العربون</dt>
                <dd className="font-bold text-white">{formatCurrency(receiptTarget.depositAmount)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-400">حساب InstaPay</dt>
                <dd className="font-bold text-white" dir="ltr">
                  {receiptTarget.instaPayAccount || 'غير محدد'}
                </dd>
              </div>
            </dl>

            {receiptTarget.depositRejectionReason && (
              <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs leading-relaxed text-red-300">
                <strong>سبب رفض الإيصال السابق:</strong> {receiptTarget.depositRejectionReason}
              </div>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleReceiptUpload}
              className="w-full text-xs text-slate-400 file:ms-3 file:rounded-lg file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-xs file:font-bold file:text-slate-200"
            />
          </div>
        )}
      </Modal>

      <RescheduleDialog
        booking={moveTarget}
        onClose={() => setMoveTarget(null)}
        onMoved={() => {
          setMoveTarget(null);
          reload();
        }}
      />
      <ReviewDialog
        booking={reviewTarget}
        onClose={() => setReviewTarget(null)}
        onSaved={() => {
          setReviewTarget(null);
          reload();
        }}
      />

      {/* View an uploaded receipt */}
      <Modal
        open={Boolean(viewingReceipt)}
        onClose={() => setViewingReceipt(null)}
        title="إيصال التحويل"
        icon="receipt"
        size="md"
      >
        {viewingReceipt?.depositReceipt ? (
          <img
            src={viewingReceipt.depositReceipt}
            alt="إيصال التحويل"
            className="mx-auto max-h-[65vh] rounded-lg object-contain"
          />
        ) : (
          <EmptyState icon="image-off" title="لا يوجد إيصال مرفوع" />
        )}
      </Modal>
    </div>
  );
}

function AppointmentCard({ booking, busy, onCancel, onUploadReceipt, onViewReceipt, onReschedule, onReview }) {
  const { t } = useI18n();
  const deposit = depositState(booking);
  const refund = refundState(booking);
  const canCancel = ['pending', 'confirmed'].includes(booking.status);
  // The booking's institution's policy, sent with the patient's own rows.
  const move = booking.reschedulePolicy ? rescheduleState(booking, booking.reschedulePolicy) : { ok: false, reason: 'closed' };
  const canReview = booking.status === 'completed' && booking.reviewed === false;
  const remote = booking.consultMode === 'video' || booking.consultMode === 'voice';

  return (
    <Panel className="flex flex-wrap items-center gap-4" data-testid="appointment-card" data-booking={booking.id}>
      <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-300">
        <span className="text-lg font-bold leading-none">{String(booking.date).slice(-2)}</span>
        <span className="text-[10px]">{arabicDayOf(booking.date)}</span>
      </div>

      {/* A floor on the details' width: on a phone the actions wrap to their
          own row instead of squeezing the doctor's name down to «د. ...». */}
      <div className="min-w-0 flex-1 basis-48">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate font-bold text-white">{booking.doctorName || 'طبيب'}</h3>
          <Badge className={statusStyle(booking.status)}>{statusLabel(booking.status)}</Badge>
          <Badge className="border-slate-600 bg-slate-800 text-slate-300">
            {typeLabel(booking.type)}
          </Badge>
          {remote && <ModeBadge mode={booking.consultMode} />}
        </div>

        <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
          <span className="flex items-center gap-1">
            <Icon name="clock" size={12} />
            {formatTime12(booking.time)}
          </span>
          {booking.clinicName && (
            <span className="flex items-center gap-1">
              <Icon name="building-2" size={12} />
              {booking.clinicName}
            </span>
          )}
          <span className="flex items-center gap-1 font-bold text-emerald-400">
            <Icon name="banknote" size={12} />
            {formatCurrency(booking.finalPrice)}
          </span>
        </p>

        {/* المرحلة 1.3: why the doctor declined, in their words. */}
        {booking.status === 'cancelled' && booking.declineReason && (
          <p className="reason-note mt-2" data-testid="decline-reason">
            <Icon name="message-square-text" size={14} className="mt-0.5 shrink-0" />
            <span>{t('appointments13.declined', { reason: booking.declineReason })}</span>
          </p>
        )}
        {Array.isArray(booking.rescheduledFrom) && booking.rescheduledFrom.length > 0 && (
          <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-cyan-300">
            <Icon name="calendar-clock" size={12} />
            {t('appointments13.moved', { n: booking.rescheduledFrom.length })}
          </p>
        )}

        {/* A cancelled booking's deposit: owed back, then returned. */}
        {refund !== 'none' && (
          <p
            className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold ${
              refund === 'refunded' ? 'text-emerald-400' : 'text-amber-400'
            }`}
          >
            <Icon name="rotate-ccw" size={12} />
            {REFUND_STATE_LABELS[refund]}
            {booking.refundAmount ? ` — ${formatCurrency(booking.refundAmount)}` : ''}
          </p>
        )}
        {deposit !== 'none' && refund === 'none' && (
          <p
            className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold ${
              deposit === 'confirmed'
                ? 'text-emerald-400'
                : deposit === 'rejected'
                  ? 'text-red-400'
                  : 'text-amber-400'
            }`}
          >
            <Icon name="wallet" size={12} />
            {DEPOSIT_STATE_LABELS[deposit]}
            {booking.depositAmount ? ` — ${formatCurrency(booking.depositAmount)}` : ''}
          </p>
        )}
      </div>

      <div className="flex w-full shrink-0 flex-wrap justify-end gap-2 sm:w-auto">
        {booking.depositReceipt && (
          <Button variant="subtle" icon="eye" onClick={onViewReceipt}>
            الإيصال
          </Button>
        )}
        {(deposit === 'awaiting_receipt' || deposit === 'rejected') && (
          <Button variant="ghost" icon="upload-cloud" onClick={onUploadReceipt}>
            رفع الإيصال
          </Button>
        )}
        {canReview && (
          <Button variant="ghost" icon="star" onClick={onReview} data-testid="review-visit">
            {t('appointments13.review')}
          </Button>
        )}
        {canCancel && move.ok && (
          <Button variant="ghost" icon="calendar-clock" onClick={onReschedule} data-testid="reschedule">
            {t('appointments13.reschedule')}
          </Button>
        )}
        {canCancel && !move.ok && booking.reschedulePolicy?.allowed === 'yes' && (move.reason === 'late' || move.reason === 'limit') && (
          <span className="self-center text-[11px] text-slate-500">{t(`appointments13.cannotMove.${move.reason}`, { hours: booking.reschedulePolicy.hoursBefore })}</span>
        )}
        {canCancel && (
          <Button variant="danger" icon="x" onClick={onCancel} loading={busy}>
            إلغاء
          </Button>
        )}
      </div>
    </Panel>
  );
}

export default PatientAppointments;
